import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ActionButton, HasAccessTo, InputFieldFactory, SearchInput } from 'app/components';
import { CONNECTION } from 'app/common/Authorization/entities';
import { RUN } from 'app/common/Authorization/permissions';
import { TranslationKeys } from 'app/translations';
import { CAN_COMPARE, CAN_COMPARE_COMPLETED } from '../../../../../../../ConnectionDetailsPage/BatchesTab/constants';
import { DropdownStyled } from '../../../../../../../../Components/EventsToolbar/EventsToolbar.styles';
import { useIntl } from 'react-intl';
import { formatMutationType } from 'app/utils/formatter/mutationFormatter';

type CompareBatchTableToolbarProps = {
  isLoading?: boolean;
  onSearch: (inputValue: string) => void;
  searchQuery?: string;
  onFilterMutationType: (filter: string) => void;
  approveBatch: () => void;
  archiveBatch: () => void;
  markAsComplete: () => void;
  markSelectedAsComplete: () => void;
  resendMutations: () => void;
  rejectMutations: () => void;
  declineBatch: () => void;
  batchStatus: 'draft' | 'pending-insurer-data' | 'compared' | 'to-be-checked';
  selectedCount?: number;
  batchSize: number;
  mutationTypes: string[];
};

export const CompareBatchTableToolbar = ({
  onSearch,
  onFilterMutationType,
  searchQuery,
  approveBatch,
  archiveBatch,
  markAsComplete,
  markSelectedAsComplete,
  resendMutations,
  rejectMutations,
  declineBatch,
  batchStatus,
  selectedCount = 0,
  batchSize,
  isLoading = false,
  mutationTypes,
}: CompareBatchTableToolbarProps) => {
  const intl = useIntl();
  const canRunConnections = HasAccessTo(CONNECTION, RUN);
  const disabledApproveButton = isLoading || selectedCount === 0 || !canRunConnections;
  const [position, setPosition] = useState({
    position: 'unset',
    backgroundColor: 'none',
    top: '0%',
    margin: '0 auto',
    padding: 'unset',
    width: 'unset',
    marginLeft: 'unset',
  });

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 340) {
        setPosition({
          margin: '0 auto',
          position: 'fixed',
          top: '64px',
          backgroundColor: '#E4F4F9',
          width: '-webkit-fill-available',
          padding: '10px 24px 10px 24px',
          marginLeft: '-24px !important',
        });
      } else {
        setPosition({
          margin: '0 auto',
          position: 'unset',
          top: 'unset',
          backgroundColor: 'unset',
          width: 'unset',
          padding: 'unset',
          marginLeft: 'unset',
        });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onChangeSearchInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );

  const onChangeMutationTypeFilter = React.useCallback(
    ({ value }) => {
      onFilterMutationType(value);
    },
    [onFilterMutationType],
  );

  const dropdownOptions = {
    reject: [
      {
        title: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectMutationsButton' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmTitle' }),
          message: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmMessage' }),
        },
        action: rejectMutations,
        disabled: disabledApproveButton,
      },
      {
        title: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectAllMutationsButton' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectConfirmTitle' }),
          message: intl.formatMessage({ id: 'connection.processes.compareBatch.rejectAllConfirmMessage' }),
        },
        action: declineBatch,
        disabled: batchSize === 0,
      },
    ],
    markAsChecked: [
      {
        title: intl.formatMessage({ id: 'connection.processes.compareBatch.markSelectedAsChecked' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'connection.processes.compareBatch.markAsCheckedConfirmTitle' }),
          message: intl.formatMessage({
            id: 'connection.processes.compareBatch.markSelectedAsCheckedConfirmMessage',
          }),
        },
        action: markSelectedAsComplete,
        disabled: disabledApproveButton,
      },
      {
        title: intl.formatMessage({ id: 'connection.processes.compareBatch.markAllAsChecked' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'connection.processes.compareBatch.markAsCheckedConfirmTitle' }),
          message: intl.formatMessage({ id: 'connection.processes.compareBatch.markAllAsCheckedConfirmMessage' }),
        },
        action: markAsComplete,
        disabled: batchSize === 0,
      },
    ],
  };

  const mutationsSelectItemsWithAllOption = React.useMemo(() => {
    const dynamicItems = mutationTypes.map((mutationType: string) => ({
      value: mutationType,
      element: formatMutationType(mutationType),
    }));

    return [
      {
        value: '',
        element: intl.formatMessage({ id: TranslationKeys.all }),
        default: true,
      },
      ...dynamicItems,
      {
        value: 'UNKNOWN',
        element: intl.formatMessage({ id: TranslationKeys.global_selectUnknown }),
      },
    ];
  }, [intl, mutationTypes]);

  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      gap={2}
      sx={{
        position,
        zIndex: 1000,
      }}
    >
      <InputFieldFactory
        key={`mutation-scheme-select`}
        disabled={isLoading}
        onChange={onChangeMutationTypeFilter}
        field={{
          type: 'select',
          name: 'MutationTypeFilter',
          label: intl.formatMessage({ id: TranslationKeys.mutation_type }),
          fullWidth: false,
          value: null,
          items: mutationsSelectItemsWithAllOption,
        }}
      />

      <SearchInput
        disabled={isLoading}
        onChange={onChangeSearchInput}
        initialSearchQuery={searchQuery}
        debounceTime={500}
      />

      {CAN_COMPARE.includes(batchStatus) && (
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
          <DropdownStyled options={dropdownOptions.reject} intl />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_archiveButton}
            disabled={disabledApproveButton}
            onClick={archiveBatch}
          />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_approveButton}
            disabled={disabledApproveButton}
            onClick={approveBatch}
          />
        </Box>
      )}

      {CAN_COMPARE_COMPLETED.includes(batchStatus) && (
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
          <DropdownStyled options={dropdownOptions.reject} intl />
          <ActionButton
            messageId={TranslationKeys.connection_processes_compareBatch_resendSelectedMutations}
            disabled={disabledApproveButton}
            onClick={resendMutations}
          />
          <DropdownStyled options={dropdownOptions.markAsChecked} intl />
        </Box>
      )}
    </Box>
  );
};
